import { getUrlWithoutQueryString } from "./common";
import HttpClient from "./http-client";
import LoadingIndication from "./loading-indication";

export default class PaginatorComponent {
    private pageNum: number;
    private pageCount: number;
    private listComponentName: string;
    private listComponentId: string;
    private listElement: any;
    private baseElement: any;
    private useAjaxForAllCalls = true;
    private fireInfiniteScroll = true;
    private pageCounter: HTMLElement;

    pageRangeBeforeShortcut = 2;

    init(element: any) {
        this.baseElement = element;
        this.useAjaxForAllCalls = element.getAttribute('data-use-ajax');
        const useInfiniteScroll = element.getAttribute('data-use-infinite-scroll').toLowerCase() == 'true';
        this.pageNum = parseInt(element.getAttribute('data-page-num'));
        this.pageCount = parseInt(element.getAttribute('data-page-count'));
        this.listComponentName = element.getAttribute('data-list-component');
        this.listComponentId = element.getAttribute('data-list-component-id');
        const componentIdSelector = this.listComponentId ? `[data-component-id="${this.listComponentId}"]` : '';
        this.listElement = document.querySelector(`[data-component=${this.listComponentName}]${componentIdSelector} [data-component-part="list-wrapper"]`);
        this.pageCounter = document.querySelector(`#pageCounter`);

        this.setButtonClick('first', () => this.loadPage(1));
        this.setButtonClick('previous', () => this.loadPage(this.pageNum - 1, this.useAjaxForAllCalls));
        this.setButtonClick('next', () => this.loadPage(this.pageNum + 1, this.useAjaxForAllCalls));
        this.setButtonClick('last', () => this.loadPage(this.pageCount, this.useAjaxForAllCalls));
        this.setButtonClick('next-ajax', () => this.loadPage(this.pageNum + 1, true, false));

        this.setButtons();
        this.generatePageItems(this.pageCount, this.pageNum);

        if (this.useAjaxForAllCalls) {
            window.addEventListener("popstate", (evt) => {
            const pageNum = Number(evt.state);
                if (!pageNum)
                    return;
                this.loadPage(pageNum, true, true);
            });
        }

        if (useInfiniteScroll) {
            this.initInfiniteScroll();
        }
    }

    private loadPage(pageNum: number, ajax = false, replace = true) {
        if (pageNum > this.pageCount)
            pageNum = this.pageCount;
        if (pageNum < 1)
            pageNum = 0;
        if (this.pageNum === pageNum)
            return;

        this.pageNum = pageNum;

        const url = getUrlWithoutQueryString();
        const params = this.getQueryParams();
        params['pageNum'] = this.pageNum;

        if (ajax) {
            const newUrl = `${url}?${this.buildQueryString(params)}`;
            params['viewComponent'] = this.getViewComponentQueryParam();
            params['isFromPaginator'] = 'true';

            LoadingIndication.show(this.listElement);

            new HttpClient().fetch(`${url}?${this.buildQueryString(params)}`)
                .then(response => response.text()
                    .then(text => {
                        if (replace) {
                            this.listElement.innerHTML = text;
                            window.scrollTo(this.listElement.offsetTop, 0);
                        }
                        else {
                            this.listElement.innerHTML += text;
                        }

                        window.history.pushState(pageNum, '', newUrl)
                    })
                    .then(() => {   
                        LoadingIndication.hide(this.listElement);
                        this.setButtons();
                        this.generatePageItems(this.pageCount, this.pageNum);
                        this.fireInfiniteScroll = true;
                    }));
        } else {
            window.location.href = `${url}?${this.buildQueryString(params)}`;        
        }

        if (this.pageCounter) {
            this.pageCounter.innerHTML = this.pageNum.toString();
        }
    }

    private initInfiniteScroll(): void {
        window.addEventListener("scroll", (evt) => {
            if (this.fireInfiniteScroll && (window.innerHeight + window.scrollY) >= document.body.offsetHeight - 800) {
                const btn = this.getButtonElement('next-ajax');
                btn.click();
                this.fireInfiniteScroll = false;
            }
        });
    }

    private getViewComponentQueryParam(): string {
        return this.listComponentId 
        ? `${this.listComponentName}_${this.listComponentId}`
        : this.listComponentName;
    }

    private getQueryParams():  { [key: string]: any; }{
        const paramPart: string = window.location.search.replace('?', '');
        const paramSplit: string[] = paramPart ? paramPart.split('&') : [];
        const params: { [key: string]: any; } = {};
        paramSplit.map(p => p.split('=')).forEach(kv => params[kv[0]] = kv[1]);
        return params;
    }

    private buildQueryString(params: { [x: string]: any; }): string {
        const query = Object.keys(params)
            .map(k => k + '=' + params[k])
            .join('&');
        return query;
    }

    private getQueryStringWithReplacedParam(paramName: string, value: any): string{
        var params = this.getQueryParams();
        params[paramName] = value;
        return this.buildQueryString(params); 
    }

    private setButtons() {
        const isFirst = this.pageNum <= 1;
        this.setButtonEnable('first', !isFirst);
        this.setButtonEnable('previous', !isFirst);

        const isLast = this.pageNum >= this.pageCount;
        this.setButtonEnable('next', !isLast);
        this.setButtonEnable('next-ajax', !isLast);
        this.setButtonEnable('last', !isLast);
    }

    private setButtonEnable(name: string, enabled: boolean) {
        if (enabled) {
            const btn = this.getButtonElement(name);
            btn && btn.removeAttribute('disabled');
        } else {
            const btn = this.getButtonElement(name);
            btn && btn.setAttribute('disabled', 'disabled');
        }
    }

    private getButtonElement(name: string) {
        return this.baseElement.querySelector(`[data-button=load-${name}-page]`);
    }

    private setButtonClick(name: string, listener: any) {
        const buttonEl = this.getButtonElement(name);
        buttonEl && buttonEl.addEventListener('click', listener);
    }

    private generatePageItems(pageCount: number, pageNumber: number) {
        if (Boolean(this.pageCounter)) {
            return;
        }

        const wrapper = document.createElement('span');
        wrapper.setAttribute('data-component-part', 'page-items');

        for (let i = 1; i < pageNumber; i++) {
            if (i > this.pageRangeBeforeShortcut && pageNumber - i > this.pageRangeBeforeShortcut) {
                wrapper.appendChild(this.createPageItemShortcut());
                i = pageNumber - (this.pageRangeBeforeShortcut + 1);
                continue;
            }
            wrapper.appendChild(this.createPageLinkItem(i));
        }

        const span = document.createElement('span');
        span.setAttribute('class', 'paginator__item--current');
        span.innerHTML = pageNumber.toString();
        wrapper.appendChild(span);

        for (let i = pageNumber + 1; i <= pageCount; i++) {
            if (i > pageNumber + this.pageRangeBeforeShortcut && pageCount - i > this.pageRangeBeforeShortcut) {
                wrapper.appendChild(this.createPageItemShortcut());
                i = pageCount - (this.pageRangeBeforeShortcut + 1);
                continue;
            }
            wrapper.appendChild(this.createPageLinkItem(i));
        }

        const pageItems = this.baseElement.querySelector('[data-component-part="page-items"]');
        pageItems.parentNode.replaceChild(wrapper, pageItems);
    }

    private createPageLinkItem(pageNum: number) {
        const span = document.createElement('span');
        span.setAttribute('class', 'paginator__item');

        const a = document.createElement('a');
        const params = this.getQueryParams();
        params['pageNum'] = pageNum;
        a.setAttribute('href', `?${this.buildQueryString(params)}`);
        
        this.setPageLinkClickAction(a, pageNum);
        a.innerHTML = pageNum.toString();
        span.appendChild(a);
        return span;
    }

    private setPageLinkClickAction(a: HTMLAnchorElement, pageNum: number) {
        if (this.useAjaxForAllCalls) {
            this.setPageAjaxLoad(a, pageNum);
        }
        else {
            this.setPageLink(a, pageNum);
        }
    }

    private setPageLink(a: HTMLAnchorElement, pageNum: number) {
        a.setAttribute('href', `${getUrlWithoutQueryString()}?${this.getQueryStringWithReplacedParam('pageNum', pageNum)}`);
    }

    setPageAjaxLoad(a: HTMLAnchorElement, pageNum: number) {
        a.addEventListener('click', evt => {
            evt.preventDefault();
            this.loadPage(pageNum, true, true);
        });
    }

    private pushHistory(pageNum: number): void {
        const url = getUrlWithoutQueryString();
        const params = this.getQueryParams();
        params['pageNum'] = this.pageNum;

        window.history.pushState(pageNum, '', `${url}?${this.buildQueryString(params)}`)
    }

    private createPageItemShortcut() {
        const span = document.createElement('span');
        span.innerHTML = '..';
        return span;
    }
}
